/**
 * ApplicationController
 *
 * Base controller class for all custom Stimulus controllers in the application.
 * Provides shared functionality through mixins:
 * - addBreadcrumb() for tracking user actions (via withBreadcrumb)
 * - reportError() for reporting errors to AppSignal (via withErrorHandler)
 *
 * Custom controllers should extend this class instead of extending Controller directly.
 *
 * @extends {import('@hotwired/stimulus').Controller}
 *
 * @example
 * // Custom controller extending ApplicationController
 * import ApplicationController from './application_controller'
 *
 * export default class extends ApplicationController {
 *   connect() {
 *     super.connect() // Call parent if needed
 *     this.addBreadcrumb('UI', 'connected')
 *   }
 *
 *   handleClick(event) {
 *     this.addBreadcrumb('UI', 'button_click', {
 *       buttonId: event.target.id
 *     })
 *     // ... rest of handler
 *   }
 *
 *   doSomethingRisky() {
 *     try {
 *       // code that might fail
 *     } catch (error) {
 *       this.reportError(error, { action: 'doSomethingRisky' })
 *     }
 *   }
 * }
 */

import { Controller } from '@hotwired/stimulus'
import { withBreadcrumb } from '../mixins/breadcrumb'
import { withErrorHandler } from '../mixins/error_handler'

// ApplicationController composes multiple mixins to provide shared functionality
// The order matters: innermost mixin is applied first
const ApplicationController = withErrorHandler(withBreadcrumb(Controller))

export default ApplicationController
